import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { PROFILE_NAME_MAX, PROFILE_PHONE_MAX } from '../../../core/constants/forms';
import { IconUser } from '../../../common/icons';
import { useAuth } from '../../../global/contexts/auth/auth';
import { useUpdateProfile } from '../contexts/profile';

import '../styles/profile.css';

// TSS vite/03 — "Mi cuenta": perfil del cliente. Lee la identidad del
// AuthProvider (cargada de GET /public/me en el bootstrap) y edita name/phone
// vía PATCH public/me (Q-101). email/document son read-only (identidad de
// login/legal — solo los cambia admin, [0051]). Una sola vista responsive:
// en desktop la identidad y el formulario van en dos columnas; en mobile,
// apiladas (styles/profile.css).

// Rol declarado → etiqueta del dominio en el portal. roles_with_data marca
// cuáles tienen datos reales (chip activo vs atenuado).
const ROLE_LABELS = {
    partner:        'Parcerías',
    donor_owner:    'Cowhousing',
    genetics_buyer: 'Crías',
};

function initials(name) {
    return (name || '?').trim().charAt(0).toUpperCase() || '?';
}

export default function ProfileView() {
    const { user, updateUser } = useAuth();
    const mutation = useUpdateProfile();

    const [name, setName]   = useState(user?.name ?? '');
    const [phone, setPhone] = useState(user?.phone ?? '');
    const [fieldErrors, setFieldErrors] = useState({});

    const initialName  = user?.name ?? '';
    const initialPhone = user?.phone ?? '';

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    // Cambios reales contra la identidad cargada (evita PATCH no-op).
    const dirty = trimmedName !== initialName.trim() || trimmedPhone !== initialPhone.trim();

    // Validación cliente (el backend revalida y responde 400 { fields }).
    const localError = useMemo(() => {
        if (!trimmedName) return { name: 'El nombre no puede quedar vacío.' };
        if (trimmedName.length > PROFILE_NAME_MAX) return { name: `Máximo ${PROFILE_NAME_MAX} caracteres.` };
        if (trimmedPhone.length > PROFILE_PHONE_MAX) return { phone: `Máximo ${PROFILE_PHONE_MAX} caracteres.` };
        return null;
    }, [trimmedName, trimmedPhone]);

    const roles = Array.isArray(user?.roles) ? user.roles : (user?.roles_with_data ?? []);
    const withData = new Set(Array.isArray(user?.roles_with_data) ? user.roles_with_data : []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!dirty || localError || mutation.isPending) return;

        // Solo los campos que cambiaron (contract: ≥1 campo). phone vaciado → null.
        const patch = {};
        if (trimmedName !== initialName.trim()) patch.name = trimmedName;
        if (trimmedPhone !== initialPhone.trim()) patch.phone = trimmedPhone === '' ? null : trimmedPhone;

        setFieldErrors({});
        mutation.mutate(patch, {
            onSuccess: (fresh) => {
                updateUser(fresh);
                setName(fresh?.name ?? '');
                setPhone(fresh?.phone ?? '');
                toast.success('Datos actualizados');
            },
            onError: (err) => {
                // ApiError — el mensaje humano va en .message; los de campo en .fields.
                if (err?.fields && typeof err.fields === 'object') setFieldErrors(err.fields);
                toast.error(err?.message || 'No se pudieron guardar los cambios');
            },
        });
    };

    const handleReset = () => {
        setName(initialName);
        setPhone(initialPhone);
        setFieldErrors({});
    };

    const nameError  = localError?.name  || fieldErrors.name;
    const phoneError = localError?.phone || fieldErrors.phone;

    return (
        <section className="perfil">
            <header className="perfil__header">
                <h1 className="perfil__title">Mi cuenta</h1>
                <p className="perfil__subtitle">Gestiona tus datos de contacto.</p>
            </header>

            <div className="perfil__grid">
                {/* Identidad — lo que el cliente no puede cambiar por sí mismo. */}
                <aside className="perfil__identity">
                    <span className="perfil__avatar">{initials(user?.name)}</span>
                    <h2 className="perfil__identity-name">{user?.name || 'Cliente'}</h2>

                    <dl className="perfil__facts">
                        <div className="perfil__fact">
                            <dt className="perfil__fact-label">Correo</dt>
                            <dd className="perfil__fact-value">{user?.email || '—'}</dd>
                        </div>
                        <div className="perfil__fact">
                            <dt className="perfil__fact-label">Documento</dt>
                            <dd className="perfil__fact-value">{user?.document || '—'}</dd>
                        </div>
                    </dl>

                    {roles.length > 0 && (
                        <div className="perfil__roles">
                            {roles.map((role) => (
                                <span
                                    key={role}
                                    className={`perfil__role ${withData.has(role) ? '' : 'perfil__role--muted'}`}
                                >
                                    {ROLE_LABELS[role] || role}
                                </span>
                            ))}
                        </div>
                    )}

                    <p className="perfil__note">
                        El correo y el documento son tu identidad de acceso: solo el equipo de
                        Manantiales puede modificarlos.
                    </p>
                </aside>

                {/* Datos editables por el cliente (PATCH public/me). */}
                <form className="perfil__form" onSubmit={handleSubmit} noValidate>
                    <h3 className="perfil__form-title">
                        <span className="perfil__form-icon"><IconUser /></span>
                        Datos de contacto
                    </h3>

                    <label className="perfil__field">
                        <span className="perfil__label">Nombre</span>
                        <input
                            className={`perfil__input ${nameError ? 'perfil__input--error' : ''}`}
                            type="text"
                            value={name}
                            maxLength={PROFILE_NAME_MAX}
                            onChange={(e) => setName(e.target.value)}
                            disabled={mutation.isPending}
                            autoComplete="name"
                        />
                        {nameError && <span className="perfil__error">{nameError}</span>}
                    </label>

                    <label className="perfil__field">
                        <span className="perfil__label">
                            Teléfono <span className="perfil__optional">(opcional)</span>
                        </span>
                        <input
                            className={`perfil__input ${phoneError ? 'perfil__input--error' : ''}`}
                            type="tel"
                            value={phone}
                            maxLength={PROFILE_PHONE_MAX}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={mutation.isPending}
                            autoComplete="tel"
                            placeholder="3001234567"
                        />
                        {phoneError && <span className="perfil__error">{phoneError}</span>}
                    </label>

                    <div className="perfil__actions">
                        <button
                            type="button"
                            className="perfil__btn perfil__btn--ghost"
                            onClick={handleReset}
                            disabled={!dirty || mutation.isPending}
                        >
                            Descartar
                        </button>
                        <button
                            type="submit"
                            className="perfil__btn perfil__btn--primary"
                            disabled={!dirty || !!localError || mutation.isPending}
                        >
                            {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
